import { getEffectiveResponse, schedulingRules, SHOW_WORKER_RESPONSE_STATUS, type FitPadding } from '../schedulingRules'
import type { Job, JobWorker, Worker, WorkerResponse } from '../types'
import { hasValidCoords, haversineKm, type LatLng } from './distance'
import { isActiveResponse } from './job-workers'
import { estimateStraightLineEtaMinutes } from './nearby-workers'

export const SELECTION_FIT_PADDING = schedulingRules.fitPaddingPx
export const SELECTION_MAX_ZOOM = schedulingRules.selectionMaxZoom

/** Minimal slice of google.maps.Map used for fitting, so the logic is testable without Google. */
export interface FittableMap {
  panTo: (position: LatLng) => void
  setZoom: (zoom: number) => void
  getZoom: () => number | undefined
  fitBounds: (bounds: google.maps.LatLngBounds, padding?: number | google.maps.Padding) => void
}

export interface FitDeps {
  createBounds: () => { extend: (point: LatLng) => void }
  /** Runs once after the camera settles (google.maps.event.addListenerOnce(map, 'idle')). */
  onceIdle: (map: FittableMap, callback: () => void) => void
}

const defaultDeps = (): FitDeps => ({
  createBounds: () => new google.maps.LatLngBounds(),
  onceIdle: (map, callback) => {
    google.maps.event.addListenerOnce(map as unknown as google.maps.Map, 'idle', callback)
  },
})

/**
 * One point: panTo + setZoom(maxZoom). Two or more: fitBounds with padding (a number or
 * per-side padding so pins are not hidden behind panels/sheets), then clamp to maxZoom.
 * Invalid points are ignored. Returns what it did.
 */
export function fitMapToPoints(
  map: FittableMap,
  points: Array<LatLng | null | undefined>,
  { padding = SELECTION_FIT_PADDING, maxZoom = SELECTION_MAX_ZOOM }: { padding?: number | FitPadding; maxZoom?: number } = {},
  deps: FitDeps = defaultDeps(),
): 'none' | 'pan' | 'bounds' {
  const valid = points.filter(hasValidCoords)
  if (valid.length === 0) return 'none'

  if (valid.length === 1) {
    map.panTo({ lat: valid[0].lat, lng: valid[0].lng })
    map.setZoom(maxZoom)
    return 'pan'
  }

  const bounds = deps.createBounds()
  valid.forEach((point) => bounds.extend({ lat: point.lat, lng: point.lng }))
  map.fitBounds(bounds as google.maps.LatLngBounds, padding)
  deps.onceIdle(map, () => {
    if ((map.getZoom() ?? 0) > maxZoom) map.setZoom(maxZoom)
  })
  return 'bounds'
}

export type MissingLocationReason = 'job_no_address' | 'job_not_geocoded' | 'job_geocode_failed' | 'job_geocoding' | 'worker_not_shared'

export interface FocusWorker {
  worker: Worker
  jobWorker: JobWorker
  /** Response with client-side expiry applied. */
  response: WorkerResponse
  located: boolean
  /** Straight-line worker -> job estimate, when both have a location. */
  route: { from: LatLng; to: LatLng; distanceKm: number; etaMinutes: number } | null
}

export interface SelectionFocus {
  job: Job
  jobHasLocation: boolean
  /** Workers on the job (declined/expired only with the response UI on). */
  workers: FocusWorker[]
  /** Points the camera should frame: job + assigned workers with a location. */
  points: LatLng[]
  missing: MissingLocationReason[]
}

/** Everything the map needs for the selected job: its pin, its workers, their routes, and gaps. */
export function resolveSelectionFocus(
  job: Job,
  workers: Worker[],
  {
    now = Date.now(),
    geocodeState,
    includeInactive = SHOW_WORKER_RESPONSE_STATUS,
  }: { now?: number; geocodeState?: 'pending' | 'failed' | null; includeInactive?: boolean } = {},
): SelectionFocus {
  const jobHasLocation = hasValidCoords(job)
  const missing: MissingLocationReason[] = []

  if (!jobHasLocation) {
    if (geocodeState === 'pending') missing.push('job_geocoding')
    else if (geocodeState === 'failed') missing.push('job_geocode_failed')
    else if (job.address?.trim()) missing.push('job_not_geocoded')
    else missing.push('job_no_address')
  }

  const workersById = new Map(workers.map((worker) => [worker.id, worker]))
  const focusWorkers: FocusWorker[] = job.workers.flatMap((jobWorker) => {
    const worker = workersById.get(jobWorker.workerId)
    const response = getEffectiveResponse(jobWorker, now)
    if (!worker || (!includeInactive && !isActiveResponse(response))) return []
    const located = hasValidCoords(worker)
    let route: FocusWorker['route'] = null
    if (located && jobHasLocation) {
      const distanceKm = haversineKm(worker, job)
      route = { from: { lat: worker.lat as number, lng: worker.lng as number }, to: { lat: job.lat as number, lng: job.lng as number }, distanceKm, etaMinutes: estimateStraightLineEtaMinutes(distanceKm) }
    }
    return [{ worker, jobWorker, response, located, route }]
  })

  if (focusWorkers.some((entry) => isActiveResponse(entry.response) && !entry.located)) missing.push('worker_not_shared')

  const points: LatLng[] = []
  if (jobHasLocation) points.push({ lat: job.lat as number, lng: job.lng as number })
  focusWorkers.forEach(({ worker, located }) => {
    if (located) points.push({ lat: worker.lat as number, lng: worker.lng as number })
  })

  return { job, jobHasLocation, workers: focusWorkers, points, missing }
}

export const MISSING_REASON_TEXT: Record<MissingLocationReason, string> = {
  job_no_address: 'This job has no address or coordinates.',
  job_not_geocoded: 'The job address has no coordinates yet.',
  job_geocoding: 'Looking up the job address…',
  job_geocode_failed: 'The job address could not be found on the map.',
  worker_not_shared: 'Worker location unavailable.',
}

/**
 * What the camera frames for a selected job: the job, its located workers and the nearest
 * few candidates (not every candidate, so a long list does not zoom the map out).
 */
export function buildSelectionFitPoints(
  focus: Pick<SelectionFocus, 'points'>,
  candidates: Array<{ worker: Pick<Worker, 'lat' | 'lng'> }>,
  nearestCount: number,
): LatLng[] {
  const nearest = candidates.slice(0, nearestCount).flatMap(({ worker }) => (hasValidCoords(worker) ? [{ lat: worker.lat, lng: worker.lng }] : []))
  return [...focus.points, ...nearest]
}
